import { useAccessRight, useForm, useTranslate } from "@/hooks";
import { Button, Slide } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/theme-iplastic";
import "ace-builds/src-noconflict/ext-language_tools";
import { ICallingListScripts } from "./CallingLineListScripts.props";
import styles from "./CallingLineListScripts.module.scss";
import cn from "classnames";
import { AlertDialog, BlockWithError, BlockWithLabel, CustomButtonWithCheckbox, CustomSelect } from "@/ui";
import { useGetCallingListsQuery, useRunScriptForListMutation, useUpdateCallingListMutation } from "@/store/RTKQuery/clsSlice";
import { useCookies } from "react-cookie";
import { ICallList } from "@/types/clsTypes";
import NoticeSingleAction from "@/components/MUI/NoticeSingleAction/NoticeSingleAction";
import { CLS } from "@/constants/accessRights";

const CallingListScripts: FC<ICallingListScripts> = ({ showScripts, setShowScripts }) => {
	const [updateCallingList] = useUpdateCallingListMutation();
	const [currentList, setCurrentList] = useState<ICallList>();
	const [cookies] = useCookies(["cls"]);
	const translate = useTranslate();
	const isAccess = useAccessRight();

	const { values, setValues } = useForm({ generalScript: "", scriptBeforeCall: "", scriptAfterCall: "" });
	const [initialValues, setInitialValues] = useState({ generalScript: "", scriptBeforeCall: "", scriptAfterCall: "", autoload: false });
	const [autoload, setAutoload] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [showNotification, setShowNotification] = useState(false);
	const [showAlertDialog, setShowAlertDialog] = useState(false);

	const [scriptTypes] = useState<
		{ value: "generalScript" | "scriptBeforeCall" | "scriptAfterCall"; title: "title_generalScript" | "title_scriptBeforeDialing" | "title_scriptAfterDialing" }[]
	>([
		{ title: "title_generalScript", value: "generalScript" },
		{ title: "title_scriptBeforeDialing", value: "scriptBeforeCall" },
		{ title: "title_scriptAfterDialing", value: "scriptAfterCall" },
	]);
	const [currentScriptType, setCurrentScriptType] = useState(scriptTypes[0]);

	const { data, isLoading, refetch } = useGetCallingListsQuery(
		{},
		{
			refetchOnMountOrArgChange: false,
			refetchOnReconnect: false,
		}
	);
	const [runScriptForList] = useRunScriptForListMutation();

	const isChanged = useMemo(
		() => ({
			generalScript: initialValues.generalScript !== values.generalScript || autoload !== initialValues.autoload,
			scriptBeforeCall: initialValues.scriptBeforeCall !== values.scriptBeforeCall,
			scriptAfterCall: initialValues.scriptAfterCall !== values.scriptAfterCall,
		}),
		[values, initialValues, autoload]
	);

	useEffect(() => {
		if (data && !isLoading && data.error !== 1) {
			const displayMode = cookies.cls ? JSON.parse(cookies.cls.displayMode) : "voice";
			const currentCallingList = data?.lists.find((el) => el.id === displayMode.selectedCallList);
			setCurrentList(currentCallingList);
			setAutoload(currentCallingList?.autoload ?? false);
			setInitialValues({
				generalScript: currentCallingList?.script ?? "",
				scriptBeforeCall: currentCallingList?.script_before_call || "",
				scriptAfterCall: currentCallingList?.script_after_call || "",
				autoload: currentList?.autoload ?? false,
			});
			setValues({
				generalScript: currentCallingList?.script ?? "",
				scriptBeforeCall: currentCallingList?.script_before_call || "",
				scriptAfterCall: currentCallingList?.script_after_call || "",
			});
		}
	}, [data, cookies.cls]);

	const closeHandler = () => setShowScripts(false);

	const handleChangeScriptType = (scriptValue: string) => {
		const newType = scriptTypes.find((el) => el.value === scriptValue);
		if (newType) setCurrentScriptType(newType);
	};

	const handleShowNotice = (message: string) => {
		setSnackbarMessage(message);
		setShowNotification(true);
	};

	const handlereset = async () => {
		setValues({ ...values, [currentScriptType.value]: "" });

		if (!data || !currentList) return;

		const changes =
			currentScriptType.value === "generalScript"
				? { script: "", autoload: false }
				: currentScriptType.value === "scriptBeforeCall"
				? { script_before_call: "" }
				: { script_after_call: "" };

		updateCallingList({ original: currentList, changes })
			.unwrap()
			.then((res) => {
				if (res.error === 0) handleShowNotice("notice_scriptResetIsSuccess");
				else handleShowNotice(`${translate("title_error")}: ${res.message}`);
			})
			.catch((err) => handleShowNotice(`${translate("title_error")}: ${JSON.stringify(err)}`));
	};

	const handleChange = (value: string) => setValues({ ...values, [currentScriptType.value]: value });

	const handleUpdateScript = () => {
		if (!data || !currentList) return;

		const changes =
			currentScriptType.value === "generalScript"
				? { script: values.generalScript, autoload: autoload }
				: currentScriptType.value === "scriptBeforeCall"
				? { script_before_call: values.scriptBeforeCall }
				: { script_after_call: values.scriptAfterCall };

		handleShowNotice("noticeSaving_inProgress");
		updateCallingList({ original: currentList, changes })
			.unwrap()
			.then((res) => {
				if (res.error === 0) handleShowNotice("noticeSaving_success");
				else handleShowNotice(`${translate("title_error")}: ${res.message}`);
			})
			.catch((err) => handleShowNotice(`${translate("title_error")}: ${JSON.stringify(err)}`));
	};

	const toggleAutoload = () => {
		setAutoload((prev) => !prev);
	};

	const handleStartScript = () => {
		if (currentList) {
			handleShowNotice("notice_scriptIsRunning");
			runScriptForList({ id: currentList.id })
				.unwrap()
				.then((res) => {
					if (res.error === 0) handleShowNotice("notice_scriptIsRun");
					else handleShowNotice(`${translate("title_error")}: ${res.message}`);
				})
				.catch((err) => handleShowNotice(`${translate("title_error")}: ${JSON.stringify(err)}`));
		}
	};

	return (
		<>
			<Slide direction="left" in={showScripts} mountOnEnter unmountOnExit>
				<div className={styles.modal} onMouseDown={closeHandler}>
					<div className={cn(styles.container)} onMouseDown={(e) => e.stopPropagation()}>
						{data?.error === 1 ? (
							<BlockWithError updateHandler={refetch} />
						) : (
							<>
								<h2 className={styles.title}>
									{translate("title_scriptsFor")} {currentList?.name}
								</h2>
								<CustomSelect variantsList={scriptTypes} title={translate("selectItem_scriptType")} setState={handleChangeScriptType} elementWidth="full" mt={"0px"} />

								<BlockWithLabel label={translate("title_scriptSettings")} sx={{ height: "100%", display: "flex", flexDirection: "column", gap: "12px" }}>
									<AceEditor
										name={currentScriptType.value}
										placeholder={translate("title_scriptForCalling")}
										mode="python"
										theme="iplastic"
										width="100%"
										height="100%"
										fontSize={11}
										value={values[currentScriptType.value]}
										onChange={handleChange}
										editorProps={{ $blockScrolling: true }}
										setOptions={{
											enableBasicAutocompletion: true,
											enableLiveAutocompletion: true,
											useWorker: false,
											wrap: true,
										}}
										style={{ flexGrow: 1 }}
									/>
									{currentScriptType.value === "generalScript" && (
										<div className={styles.actionsBlock}>
											<CustomButtonWithCheckbox
												onClick={toggleAutoload}
												title={autoload ? translate("title_scriptActive") : translate("title_scriptInactive")}
												buttonText={autoload ? translate("button_scriptIsActive") : translate("button_scriptIsInactive")}
												disabled={currentScriptType.value === "generalScript" && values.generalScript.length === 0}
												isActive={autoload}
											/>
											<Button
												variant="outlined"
												component="label"
												onClick={handleStartScript}
												sx={{ fontSize: 11, width: "100%" }}
												disabled={values.generalScript.length === 0}
											>
												{translate("button_runNow")}
											</Button>
										</div>
									)}
								</BlockWithLabel>

								<div className={styles.actionsBlock}>
									{isAccess(CLS.LISTS_MODIFY) && (
										<>
											<Button
												variant="outlined"
												disabled={
													currentScriptType.value === "generalScript"
														? !isChanged.generalScript
														: currentScriptType.value === "scriptBeforeCall"
														? !isChanged.scriptBeforeCall
														: !isChanged.scriptAfterCall
												}
												sx={{ fontSize: 11, width: "100%" }}
												onClick={handleUpdateScript}
											>
												{translate("button_save")}
											</Button>

											<Button
												variant="outlined"
												disabled={values[currentScriptType.value].length === 0}
												sx={{ fontSize: 11, width: "100%" }}
												onClick={() => setShowAlertDialog(true)}
												color="error"
											>
												{translate("button_delete")}
											</Button>
										</>
									)}
								</div>
							</>
						)}

						<div className={styles.tagClose} onClick={closeHandler}>
							{translate("button_close")}
						</div>
					</div>
				</div>
			</Slide>

			<AlertDialog
				showAlertDialog={showAlertDialog}
				setShowAlertDialog={setShowAlertDialog}
				submitHandler={handlereset}
				title={translate("title_resetScript")}
				description="dialog_resetScript"
			/>

			<NoticeSingleAction
				severity={snackbarMessage.toLowerCase().includes("error") || snackbarMessage.toLowerCase().includes("ошибка") ? "error" : "info"}
				showNotification={showNotification}
				setShowNotification={setShowNotification}
				title={snackbarMessage}
			/>
		</>
	);
};

export default CallingListScripts;
